import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { sendSms, getSmsCredits } from "@/lib/smsService";

export function SendSmsDialog({ open, onOpenChange, recipient, company }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);

  const senderId = company?.settings?.sms_sender_id || "Training";
  const maxChars = 1600;

  useEffect(() => {
    if (open && company?.id) {
      setLoadingCredits(true);
      getSmsCredits(company.id)
        .then((data) => setCredits(data))
        .catch(() => setCredits(null))
        .finally(() => setLoadingCredits(false));
    }
  }, [open, company?.id]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (credits?.balance === 0) {
      toast.error("No SMS credits remaining");
      return;
    }

    try {
      setSending(true);
      await sendSms({
        message: message.trim(),
        recipientUserId: recipient.id,
        companyId: company?.id,
      });
      toast.success(`SMS sent to ${recipient.display_name}`);
      setMessage("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send SMS
          </DialogTitle>
          <DialogDescription>
            Send an SMS message to {recipient?.display_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Recipient info */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium">{recipient?.display_name}</p>
              <p className="text-xs text-muted-foreground">{recipient?.phone_number}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
              {loadingCredits ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Badge variant={credits?.balance > 0 ? "secondary" : "destructive"} className="text-xs">
                  {credits?.balance ?? 0} credits
                </Badge>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={maxChars}
              rows={4}
              className="resize-none"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>From: {senderId}</span>
              <span className={message.length > maxChars * 0.9 ? "text-orange-500" : ""}>
                {message.length}/{maxChars}
              </span>
            </div>
          </div>

          {credits?.balance === 0 && (
            <p className="text-sm text-destructive">
              No SMS credits remaining. Purchase more from Company Settings.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !message.trim() || credits?.balance === 0}
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-2" />
                Send SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
